import { Badge, Button, Checkbox, Group, Loader, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AdminPortalPartnerRO } from '@routellers/types/dist/partners-portal/admin/admin-portal-partner.ro';
import { PortalPartnerStatusEnum } from '@routellers/types/dist/partners-portal/enum/portal-partner-status.enum';
import { PortalPartnerTypeEnum } from '@routellers/types/dist/partners-portal/enum/portal-partner-type.enum';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { CityQuickSelect, parseCityFilter } from '../components/city-quick-select';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { PartnerCreateDialog } from '../editors/partner-create-dialog';
import { PartnerEditor } from '../editors/partner-editor';
import { slicePage } from '../utils/list';
import {
    portalPartnerStatusColors,
    portalPartnerStatusLabel,
} from '../utils/portal-partner-status';

export const VenuesPage = () => {
    const { t } = useTranslation();
    const [venues, setVenues] = useState<AdminPortalPartnerRO[]>([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [parsing, setParsing] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [cityFilter, setCityFilter] = useState('all');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [editorPartner, setEditorPartner] = useState<AdminPortalPartnerRO | null>(null);
    const [creating, setCreating] = useState(false);

    const cityId = parseCityFilter(cityFilter);

    const load = useCallback(async () => {
        setLoading(true);

        try {
            const { data } = await api.partnersPortalAdminApi.partners({
                type: PortalPartnerTypeEnum.VENUE,
                ...(cityId ? { cityId } : {}),
            });
            setVenues(data || []);
            setSelectedIds([]);
            setPage(1);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setVenues([]);
        } finally {
            setLoading(false);
        }
    }, [cityId]);

    useEffect(() => {
        load();
    }, [load]);

    const pageRows = slicePage(venues, page);

    const allSelected = useMemo(
        () => pageRows.length > 0 && pageRows.every((v) => selectedIds.includes(v.id)),
        [pageRows, selectedIds],
    );

    const toggleAll = () => {
        if (allSelected) {
            const pageIds = new Set(pageRows.map((v) => v.id));
            setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...pageRows.map((v) => v.id)])]);
        }
    };

    const toggleOne = (id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    const parseCity = async () => {
        let id = cityId;

        if (!id) {
            const raw = window.prompt(t('pages.venues.promptCityId'));

            if (!raw) return;
            id = Number(raw);
            if (!Number.isFinite(id) || id <= 0) {
                notifications.show({ color: 'red', message: t('pages.venues.invalidCityId') });

                return;
            }
        }

        if (!window.confirm(t('pages.venues.confirmParse', { id }))) {
            return;
        }

        setParsing(true);

        try {
            const { data } = await api.partnersPortalAdminApi.parseVenuesByCity({
                data: { cityId: id },
            });

            notifications.show({
                color: 'green',
                title: t('pages.venues.parsedTitle', {
                    city: data?.cityTitle || `city #${id}`,
                }),
                message: t('pages.venues.parsedMessage', {
                    found: data?.found ?? 0,
                    created: data?.created ?? 0,
                    closed: data?.closed ?? 0,
                    skipped: data?.skipped ?? 0,
                }),
                autoClose: 8000,
            });
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setParsing(false);
        }
    };

    const bulkStatus = async (status: PortalPartnerStatusEnum) => {
        if (!selectedIds.length) {
            notifications.show({ color: 'red', message: t('pages.venues.selectVenuesFirst') });

            return;
        }

        if (
            !window.confirm(
                t('pages.venues.confirmBulkStatus', {
                    status: portalPartnerStatusLabel(t, status),
                    count: selectedIds.length,
                }),
            )
        ) {
            return;
        }

        setBulkLoading(true);

        try {
            const { data } = await api.partnersPortalAdminApi.bulkUpdateStatus({
                data: { ids: selectedIds, status },
            });

            notifications.show({
                color: 'green',
                message: t('pages.venues.bulkUpdated', { count: data?.updated ?? 0 }),
            });
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBulkLoading(false);
        }
    };

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.venues.title')}
                action={
                    <Group gap='sm'>
                        <Button variant='light' loading={parsing} onClick={() => void parseCity()}>
                            {t('pages.venues.parseByCity')}
                        </Button>
                        <Button onClick={() => setCreating(true)}>
                            {t('pages.venues.create')}
                        </Button>
                    </Group>
                }
            />

            <Group gap='sm' wrap='wrap'>
                <CityQuickSelect value={cityFilter} onChange={setCityFilter} />
                <Button
                    variant='default'
                    disabled={!selectedIds.length}
                    loading={bulkLoading}
                    onClick={() => void bulkStatus(PortalPartnerStatusEnum.INVITE_SENT)}
                >
                    {t('pages.venues.markInviteSent', { count: selectedIds.length })}
                </Button>
                <Button
                    variant='default'
                    disabled={!selectedIds.length}
                    loading={bulkLoading}
                    onClick={() => void bulkStatus(PortalPartnerStatusEnum.BLOCKED)}
                >
                    {t('pages.venues.blockSelected')}
                </Button>
                <Button
                    variant='default'
                    disabled={!selectedIds.length}
                    loading={bulkLoading}
                    onClick={() => void bulkStatus(PortalPartnerStatusEnum.REJECTED)}
                >
                    {t('pages.venues.rejectSelected')}
                </Button>
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={900}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th w={40}>
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={
                                                selectedIds.length > 0 &&
                                                pageRows.some((v) => selectedIds.includes(v.id)) &&
                                                !allSelected
                                            }
                                            onChange={toggleAll}
                                            aria-label={t('common.selectAll')}
                                        />
                                    </Table.Th>
                                    <Table.Th>{t('pages.venues.columns.venue')}</Table.Th>
                                    <Table.Th>{t('pages.venues.columns.email')}</Table.Th>
                                    <Table.Th>{t('pages.venues.columns.city')}</Table.Th>
                                    <Table.Th>{t('pages.venues.columns.promocode')}</Table.Th>
                                    <Table.Th ta='right'>
                                        {t('pages.venues.columns.commission')}
                                    </Table.Th>
                                    <Table.Th>{t('pages.venues.columns.status')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {pageRows.map((row) => (
                                    <Table.Tr
                                        key={row.id}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setEditorPartner(row)}
                                    >
                                        <Table.Td onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={selectedIds.includes(row.id)}
                                                onChange={() => toggleOne(row.id)}
                                                aria-label={`${t('common.select')} #${row.id}`}
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <EntityThumb
                                                src={row.avatarUrl}
                                                name={row.companyName || row.name}
                                                subtitle={row.googleAddress || row.name}
                                                radius='md'
                                            />
                                        </Table.Td>
                                        <Table.Td>{row.email || '—'}</Table.Td>
                                        <Table.Td>{row.cityTitle || '—'}</Table.Td>
                                        <Table.Td>{row.promocode || '—'}</Table.Td>
                                        <Table.Td ta='right'>{row.commissionPercent}%</Table.Td>
                                        <Table.Td>
                                            <Badge
                                                color={
                                                    portalPartnerStatusColors[row.status] || 'gray'
                                                }
                                                variant='light'
                                            >
                                                {portalPartnerStatusLabel(t, row.status)}
                                            </Badge>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                                {!pageRows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={7}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.venues.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={venues.length} onChange={setPage} />
                </>
            )}

            <PartnerCreateDialog
                opened={creating}
                type={PortalPartnerTypeEnum.VENUE}
                onClose={() => setCreating(false)}
                onCreated={() => {
                    load();
                }}
            />

            <PartnerEditor
                opened={editorPartner != null}
                partner={editorPartner}
                onClose={() => setEditorPartner(null)}
                onSaved={load}
                onDeleted={load}
            />
        </Stack>
    );
};
